import { gsap, TweenVars } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface LayerPreset {
  fromVars: TweenVars,
  toVars: TweenVars,
  scrollTriggerInstanceVars?: gsap.plugins.ScrollTriggerInstanceVars,
}

export class ParallaxHelper {
  private static readonly parallaxSelector: string = '[data-js-parallax]';
  private static readonly containerSelector: string = '[data-js-parallax-container]';
  private static readonly slaveSelector: string = '[data-js-parallax-slave]';

  private static readonly layerPresets: Record<string, LayerPreset> = {
    background: {
      fromVars: { '--parallax-offset': '-22.5rem' },
      toVars: { '--parallax-offset': '22.5rem' },
    },
    glyph: {
      fromVars: { '--parallax-offset': '-15rem' },
      toVars: { '--parallax-offset': '15rem' },
    },
    stageBackground: {
      fromVars: { '--parallax-offset': '0' },
      toVars: { '--parallax-offset': '30rem', '--dim': 0 },
      scrollTriggerInstanceVars: {
        start: 0,
      },
    },
    stageGlyph: {
      fromVars: { '--parallax-offset': '0' },
      toVars: { '--parallax-offset': '20rem' },
      scrollTriggerInstanceVars: {
        start: 0,
      },
    },
  };

  public static initParallax(): void {
    const { parallaxSelector, containerSelector } = ParallaxHelper;

    gsap.utils.toArray(parallaxSelector).forEach((parallaxElement: HTMLElement) => {
      const layerPreset = ParallaxHelper.getLayerPreset(parallaxElement);

      if (!layerPreset) {
        return null;
      }

      const trigger = parallaxElement.closest(containerSelector) as HTMLElement || document.body;
      const endTrigger = this.findSlaveContainerElement(trigger) || trigger;

      gsap.fromTo(parallaxElement,
        layerPreset.fromVars,
        {
          ...layerPreset.toVars,
          ease: 'none',
          scrollTrigger: {
            ...layerPreset.scrollTriggerInstanceVars,
            trigger,
            endTrigger,
            scrub: true,
          },
        }
      );
    });
  }

  public static getLayerPreset(parallaxElement: HTMLElement): LayerPreset {
    const { layerPresets } = ParallaxHelper;
    const layerName = parallaxElement.dataset.layerName || 'glyph';

    if (!layerPresets[layerName]) { return null; }

    return layerPresets[layerName];
  }

  public static initSlave() {
    const { slaveSelector } = ParallaxHelper;

    document.querySelectorAll(slaveSelector).forEach((slaveElement: HTMLElement) => {
      const masterElement = ParallaxHelper.findMasterElement(slaveElement);

      if (!masterElement) {
        return null;
      }

      const setSlaveTransformProperty = () => {
        slaveElement.style.setProperty('--parallax-offset', masterElement.style.getPropertyValue('--parallax-offset'));
      };

      setSlaveTransformProperty();

      const observer = new MutationObserver(setSlaveTransformProperty);

      observer.observe(masterElement, {
        attributes: true,
        attributeFilter: ['style'],
      });
    });
  }

  public static findMasterElement(slaveElement: HTMLElement): HTMLElement {
    const { parallaxSelector, containerSelector } = ParallaxHelper;

    return slaveElement.closest(containerSelector)?.previousElementSibling?.querySelector(parallaxSelector) || null;
  }

  public static findSlaveContainerElement(masterContainerElement: HTMLElement): HTMLElement {
    const { slaveSelector, containerSelector } = ParallaxHelper;
    const presumedSlaveContainerElement = masterContainerElement.nextElementSibling as HTMLElement;

    if (!presumedSlaveContainerElement?.matches(containerSelector) || !presumedSlaveContainerElement.querySelector(slaveSelector)) {
      return null;
    }

    return presumedSlaveContainerElement;
  }

  public static init(): void {
    ParallaxHelper.initParallax();
    ParallaxHelper.initSlave();
  }
}
